import { Component, ElementRef, HostBinding, HostListener, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { CustomRollbar, RollbarService } from 'src/app/shared/providers/rollbarlog.service';
import { ProductAnalyticsService, productMegabytes } from 'src/app/shared/services/product-analytics.service';
import { RenderDropZoneComponent, RenderZoneUpload } from '../drop-zone/render-drop-zone.component';
import { RenderLeaveDialogComponent, RenderLeaveDialogData } from '../leave-dialog/render-leave-dialog.component';
import { RENDER_SAMPLE_DRAG_TYPE } from '../render-drag';
import { RenderExporter, renderGuideApp } from '../render-export-guide';
import { RenderFlowService, RenderPreviewInput, RenderReady } from '../render-flow.service';
import { RenderHelpService } from '../render-help.service';
import { renderDialogPanes } from '../render-home';
import { easeElementScrollTo, easeWindowScrollTo } from '../render-scroll';
import {
  checkModelTextures, collectInputFiles, fileExtension, inspectUpload, isModelFile, RenderUploadFile, RenderUploadModel, RenderUploadProblem, withAddedFiles,
} from '../render-upload.inspector';
import { prepareFiles, renderArchiveLimitBytes, RenderModelOption, renderPickerAccept, RenderPrepareProgress } from '../render-upload.prepare';
import { canInflate } from '../render-zip';
import { RENDER_PRICE_USD, RENDER_SAMPLES, RENDER_TEXTURE_EXTENSIONS, RenderSample, RenderSampleId } from '../render.data';

// How long the samples wait for the list of the user's models before showing in full.
const SAMPLES_LIST_WAIT_MS = 3000;

// A model that waits for textures (render-fbx-textures.ts).
interface RenderTextureGap {
  model: RenderUploadModel;
  missing: string[];
  found: string[];
  // Missing before, dropped or picked since: marked "added" in the list.
  added: string[];
  // The line the upload will carry ('Using rock_LOD0.fbx, the most detailed of 6 versions.').
  note?: string;
  archive?: string;
}

// Step 1: the drop zone (file or folder) and the three samples. Once a model is ready to go, its
// upload starts and the model's processing page opens at once, "Uploading" the first line of its
// stages card (owner, 2026-10-20: the card from the first byte, not after the last). The upload
// itself runs in RenderFlowService.beginUpload. Nothing leaves the device in the mock build: the
// upload is timed, the preview of a user's file is a sample's frame under its name.
//
// Samples are dragged into the zone like files (render-drag.ts). A mouse click shows how instead
// of starting; a tap or the keyboard drops the sample for the user.
//
// Before anything goes: a ZIP is unpacked on the page and a drop with several models gets one or
// asks which (prepareFiles), then an FBX is read for its textures (checkModelTextures) — one that
// asks for files the drop does not have waits here for them instead of uploading into a sure
// renderModelError. The unpacking and the question play in the zone; under it only what needs an
// answer (owner, 2026-09-30, the proposals artifact): the texture checklist — amber, a warning the
// user may pass — or the refusal, red and docked to the zone, with the button that starts its fix.
//
// Leaving the page drops an archive being unpacked (the designer's rule for the upload, 2026-10-16,
// which now lives on the processing page and survives moving around the site), so while one is:
// a line under the zone says so, the tab's title says "Unpacking", closing the tab gets the
// browser's own question (beforeunload) and leaving within the site ours (RenderUploadLeaveGuard,
// confirmLeave) — the browser's Back included, which is a route change here, not an unload.
@Component({
  selector: 'app-render-upload-step',
  templateUrl: './render-upload-step.component.html',
})
export class RenderUploadStepComponent implements OnInit, OnDestroy {
  @HostBinding('class') public readonly hostClass = 'render-step step-upload';

  public readonly samples = RENDER_SAMPLES;
  public readonly price = RENDER_PRICE_USD;
  public readonly benefits = ['Automatic positioning and lighting', 'Portrait and landscape previews', 'No payment card to get started'];

  public problem: RenderUploadProblem | null = null;
  // An FBX waiting for the textures it asks for; files dropped or picked now join it.
  public textureGap: RenderTextureGap | null = null;
  // A ZIP of textures works too, where the browser can unpack one: prepareFiles takes its textures
  // for the waiting model.
  public readonly textureAccept = renderPickerAccept([...RENDER_TEXTURE_EXTENSIONS, ...(canInflate() ? ['.zip'] : [])]);
  // Several different models in the drop: the user picks one, and the drop is prepared again.
  public modelChoice: { files: RenderUploadFile[]; models: RenderModelOption[]; selected: string; source?: string } | null = null;
  // The zone's card: an archive being unpacked, or a sample's thumbnail landing before its upload
  // starts. Set through the accessors below, which keep `inFlight` and the tab's title in step.
  private zoneUnpacking: RenderPrepareProgress | null = null;
  private zoneUpload: RenderZoneUpload | null = null;
  private readonly inFlight = new BehaviorSubject<boolean>(false);
  // The bubble under a sample: how to drag it, or — after a drop that missed — where to let go.
  public hint: { sampleId: RenderSampleId; missed: boolean } | null = null;
  public draggingSample: RenderSample | null = null;
  // The samples in full (a first-timer, or opened from the folded line), folded to one line once
  // the user has a model of their own, null until the list of models says which.
  public samplesShown: boolean | null = null;
  // Folded for this visit (a model of the user's own is in the list): the heading opens and closes them.
  public samplesFoldable = false;
  // "Your library · 3 models" on the samples' row: every model its page lists — the user's own, no
  // sample walked through (owner, 2026-10-23) — and the unopened result the profile button's dot
  // stands for. Null while the library is empty.
  public readonly library$: Observable<{ count: number; ready: RenderReady | null; label: string } | null> = combineLatest([this.flow.ownJobs$, this.flow.ready$]).pipe(
    map(([jobs, ready]) => {
      if (!jobs.length) {
        return null;
      }
      const models = `${jobs.length} ${jobs.length === 1 ? 'model' : 'models'}`;
      const news = ready ? `, ${ready.failed ? 'a render failed' : 'something new to see'}` : '';
      return { count: jobs.length, ready, label: `Your library, ${models}${news}` };
    }),
  );

  @ViewChild(RenderDropZoneComponent) private zone?: RenderDropZoneComponent;
  @ViewChild('gapScroll') private gapScroll?: ElementRef<HTMLElement>;

  // The texture list's rows out of sight below its box: "↓ 9 more" until the end is reached. The
  // rows' own box is watched, so a texture added (the list growing) counts too.
  public gapBelow = 0;
  private gapResize?: ResizeObserver;

  @ViewChild('gapList') private set gapList(list: ElementRef<HTMLElement> | undefined) {
    this.gapResize?.disconnect();
    this.gapResize = undefined;
    if (!list || typeof ResizeObserver === 'undefined') {
      this.gapBelow = 0;
      return;
    }
    this.gapResize = new ResizeObserver(() => this.ngZone.run(() => this.measureGapList()));
    this.gapResize.observe(list.nativeElement);
  }

  private filesSubscription?: Subscription;
  private lastPointerType = '';
  private droppedInZone = false;
  // Bumped by every new start, so an unpacking or a texture check that ends late is dropped.
  private run = 0;
  // Left the page while the zone was scrolling into view or a thumbnail was in the air.
  private destroyed = false;

  constructor(
    private readonly router: Router,
    private readonly flow: RenderFlowService,
    private readonly ngZone: NgZone,
    private readonly help: RenderHelpService,
    private readonly dialog: MatDialog,
    private readonly productAnalytics: ProductAnalyticsService,
    @Inject(RollbarService) private readonly rollbar: CustomRollbar,
  ) {}

  ngOnInit(): void {
    // Files dropped on the landing are prepared here, where the zone can show the unpacking.
    const pending = this.flow.takePendingFiles();
    if (pending) {
      this.prepare(pending);
    }
    // A sample picked on a landing uploads as if it had been dropped here.
    const sample = this.flow.takePendingSample();
    if (sample && !pending) {
      this.beginUpload({ sample });
    }
    this.filesSubscription = this.flow.files$.subscribe(() => {
      const files = this.flow.takePendingFiles();
      if (files) {
        this.cancelUpload();
        this.prepare(files);
      }
    });
    this.decideSamples();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.filesSubscription?.unsubscribe();
    this.gapResize?.disconnect();
    this.cancelUpload();
    this.unpacking = null;
  }

  public get upload(): RenderZoneUpload | null {
    return this.zoneUpload;
  }

  public set upload(upload: RenderZoneUpload | null) {
    this.zoneUpload = upload;
    this.syncInFlight();
  }

  public get unpacking(): RenderPrepareProgress | null {
    return this.zoneUnpacking;
  }

  public set unpacking(unpacking: RenderPrepareProgress | null) {
    this.zoneUnpacking = unpacking;
    this.syncInFlight();
  }

  public get busy(): boolean {
    return !!this.upload;
  }

  // An archive unpacking: what leaving the page would lose. An upload is not the page's — it goes
  // on wherever the user goes.
  public get uploadInFlight(): boolean {
    return !!this.zoneUnpacking;
  }

  // Closing the tab, reloading, typing another address: the browser asks with its own words.
  @HostListener('window:beforeunload', ['$event'])
  public onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.uploadInFlight) {
      event.preventDefault();
      // Older Chrome and Safari ask only when this is set.
      event.returnValue = '';
    }
  }

  // Leaving within the site while an archive unpacks (RenderUploadLeaveGuard): resolves true to go.
  // Should the unpacking end while the question is open, it closes by itself and lets the user go.
  public confirmLeave(): Promise<boolean> {
    if (!this.uploadInFlight) {
      return Promise.resolve(true);
    }
    const dialogRef = this.dialog.open<RenderLeaveDialogComponent, RenderLeaveDialogData, 'leave' | undefined>(RenderLeaveDialogComponent, {
      data: { name: this.unpacking?.name || 'Your archive' },
      autoFocus: 'dialog',
      panelClass: renderDialogPanes('render-unlock-pane'),
      ariaLabelledBy: 'render-leave-title',
    });
    const settled = this.inFlight.pipe(filter((inFlight) => !inFlight), take(1)).subscribe(() => dialogRef.close('leave'));
    return new Promise((resolve) => {
      dialogRef.afterClosed().subscribe((result) => {
        settled.unsubscribe();
        this.productAnalytics.track('leave_dialog_shown', { params: { left: result === 'leave' } });
        resolve(result === 'leave');
      });
    });
  }

  // DEV: under the render simulator a file of the user's own is really uploaded, so the mock's
  // "validation only" note does not apply.
  public get isSimulator(): boolean {
    return this.flow.isSimulator;
  }

  // What the zone asks for by name: the sample being dragged, or the one whose hint is open.
  public get inviteName(): string | null {
    if (this.draggingSample) {
      return this.draggingSample.fileName;
    }
    if (this.textureGap) {
      return this.textureGap.missing.length === 1 ? this.textureGap.missing[0] : 'the missing textures';
    }
    return this.hint ? this.samples.find((sample) => sample.id === this.hint?.sampleId)?.fileName ?? null : null;
  }

  // The zone's formats line while a model waits for textures.
  public get zoneHint(): string | null {
    return this.textureGap ? 'PNG, JPG, TGA, EXR · or a folder with them' : null;
  }

  // "chair.fbx is missing 1 of 3 textures", "… is still missing 2 of 31 textures".
  public get gapTitle(): string {
    const gap = this.textureGap;
    if (!gap) {
      return '';
    }
    const total = gap.missing.length + gap.found.length;
    const still = gap.added.length ? 'still ' : '';
    return total === 1 ? `${gap.model.fileName} is missing its texture` : `${gap.model.fileName} is ${still}missing ${gap.missing.length} of ${total} textures`;
  }

  // Missing first, then the ones just added, then the ones found — all of them; the list scrolls.
  public get gapRows(): Array<{ name: string; state: 'missing' | 'added' | 'found' }> {
    const gap = this.textureGap;
    if (!gap) {
      return [];
    }
    const added = new Set(gap.added.map((name) => name.toLowerCase()));
    return [
      ...gap.missing.map((name) => ({ name, state: 'missing' as const })),
      ...gap.found.filter((name) => added.has(name.toLowerCase())).map((name) => ({ name, state: 'added' as const })),
      ...gap.found.filter((name) => !added.has(name.toLowerCase())).map((name) => ({ name, state: 'found' as const })),
    ];
  }

  public measureGapList(): void {
    const box = this.gapScroll?.nativeElement;
    const row = box?.querySelector('li');
    if (!box || !row) {
      this.gapBelow = 0;
      return;
    }
    const pitch = row.getBoundingClientRect().height + parseFloat(getComputedStyle(row.parentElement as Element).rowGap || '0');
    // Every row not wholly in sight counts, the cut one included ("6 rows … ↓ 9 more" of 15);
    // a couple of pixels left is the end.
    const below = box.scrollHeight - box.scrollTop - box.clientHeight;
    this.gapBelow = below > 2 ? Math.ceil((below - 2) / pitch) : 0;
  }

  // "↓ 9 more": to the end of the list (eased by hand — smoothscroll.js breaks behavior: 'smooth').
  public scrollGapList(): void {
    const box = this.gapScroll?.nativeElement;
    if (box) {
      easeElementScrollTo(box, box.scrollHeight);
    }
  }

  public get gapConsequence(): string {
    return this.textureGap?.missing.length === 1
      ? 'Without it, the parts that use it render with no texture.'
      : 'Without them, the parts that use them render with no texture.';
  }

  // The program the waiting FBX came from, when its header names it.
  public get gapExporter(): RenderExporter | undefined {
    return this.textureGap?.model.fbx?.exporter;
  }

  // "Next time, set Path Mode to Copy and …": that program's one setting, or the general advice.
  public get gapPackHint(): string {
    const exporter = this.gapExporter;
    return (exporter && renderGuideApp(exporter.app).packHint) || 'pack them into the FBX.';
  }

  // The export guide (render-help-dialog): from the zone's formats line and a refused drop.
  public openGuide(): void {
    this.help.open();
  }

  // "Show me how": the guide at the step that packs the textures in, for the FBX's program.
  public showPackGuide(): void {
    this.help.open({ exporter: this.gapExporter, step: 'pack' });
  }

  // "Collier…usdz": the name's start and its extension, for three samples across a phone (her
  // Figma 28529:22052).
  public shortName(fileName: string): string {
    const dot = fileName.lastIndexOf('.');
    const base = dot > 0 ? fileName.slice(0, dot) : fileName;
    const extension = dot > 0 ? fileName.slice(dot + 1) : '';
    return base.length <= 8 ? fileName : `${base.slice(0, 7)}…${extension}`;
  }

  public trackBySampleId(index: number, sample: RenderSample): string {
    return sample.id;
  }

  // The heading stays where it is; the row opens and closes under it.
  public toggleSamples(): void {
    this.samplesShown = !this.samplesShown;
  }

  // Once per visit, so the block never folds under the user: a model of their own in the list
  // (the backend's, when there is one) folds it; samples they walked through do not count.
  private async decideSamples(): Promise<void> {
    if (this.flow.hasBackendList) {
      await this.flow.whenListed(SAMPLES_LIST_WAIT_MS);
    }
    if (!this.destroyed) {
      this.samplesFoldable = this.flow.jobs.some((job) => job.source.kind === 'upload');
      this.samplesShown = !this.samplesFoldable;
    }
  }

  // --- the user's files ---

  public onFiles(files: RenderUploadFile[]): void {
    this.prepare(files);
  }

  public onTexturesPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = collectInputFiles(input.files);
    input.value = '';
    if (files.length > 0) {
      this.onFiles(files);
    }
  }

  // For a model this reads wrong: the renderer still has the last word.
  public uploadAnyway(): void {
    const gap = this.textureGap;
    if (gap) {
      this.beginUpload({ upload: gap.model }, undefined, gap.note);
    }
  }

  public chooseModel(): void {
    const choice = this.modelChoice;
    if (choice) {
      this.prepare(choice.files, choice.selected);
    }
  }

  public selectModel(path: string): void {
    if (this.modelChoice) {
      this.modelChoice = { ...this.modelChoice, selected: path };
    }
  }

  public dismissChoice(): void {
    this.modelChoice = null;
  }

  // The refusal's button: another file, or the unpacked folder.
  public fixProblem(): void {
    if (this.problem?.fix === 'folder') {
      this.zone?.chooseFolder();
    } else {
      this.zone?.chooseFile();
    }
  }

  public cancelUnpacking(): void {
    this.run++;
    this.unpacking = null;
  }

  // --- samples ---

  public onSamplePointerDown(event: PointerEvent): void {
    this.lastPointerType = event.pointerType;
  }

  // Click, tap, Enter or Space. A mouse is shown the drag; touch and the keyboard, which cannot
  // drag, get the drop done for them.
  public onSampleActivate(sample: RenderSample, event: Event, image: HTMLImageElement): void {
    const byMouse = event instanceof MouseEvent && event.detail > 0 && (this.lastPointerType === 'mouse' || this.lastPointerType === 'pen');
    this.lastPointerType = '';
    if (this.busy) {
      return;
    }
    if (byMouse) {
      this.hint = { sampleId: sample.id, missed: false };
      this.zone?.playDragDemo(image);
      return;
    }
    this.dropForUser(sample, image);
  }

  public onSampleDragStart(sample: RenderSample, event: DragEvent): void {
    if (this.busy || !event.dataTransfer) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.setData(RENDER_SAMPLE_DRAG_TYPE, sample.id);
    event.dataTransfer.effectAllowed = 'copy';
    this.hint = null;
    this.problem = null;
    this.droppedInZone = false;
    this.draggingSample = sample;
  }

  // The zone's drop comes first; a release anywhere else points at the box.
  public onSampleDragEnd(sample: RenderSample): void {
    this.draggingSample = null;
    if (!this.droppedInZone && !this.busy) {
      this.hint = { sampleId: sample.id, missed: true };
    }
  }

  public onSampleDropped(): void {
    const sample = this.draggingSample;
    if (!sample || this.busy) {
      return;
    }
    this.droppedInZone = true;
    this.beginUpload({ sample });
  }

  // "Do it for me" in the hint, a tap, or the keyboard: bring the zone into view, fly the
  // thumbnail in, upload.
  public async dropForUser(sample: RenderSample, image: HTMLImageElement): Promise<void> {
    this.hint = null;
    const target = document.querySelector('.step-upload .zone__target');
    if (target) {
      const rect = target.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        await easeWindowScrollTo(window.scrollY + rect.top - 24, 350);
      }
    }
    if (!this.busy && !this.destroyed) {
      this.beginUpload({ sample }, image);
    }
  }

  @HostListener('document:pointerdown', ['$event'])
  public onDocumentPointerDown(event: PointerEvent): void {
    if (this.hint && !(event.target as Element | null)?.closest?.('.sample-item')) {
      this.hint = null;
    }
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    this.hint = null;
  }

  // --- the upload ---

  // The drop → archives opened and a model picked (render-upload.prepare.ts) → the set inspected →
  // the FBX's textures checked → the upload. A newer drop, a sample or Cancel overtakes any await.
  private async prepare(dropped: RenderUploadFile[], chosenPath?: string): Promise<void> {
    const run = ++this.run;
    const gap = this.textureGap;
    const stale = () => run !== this.run || this.destroyed;
    this.problem = null;
    this.modelChoice = null;
    // A drop (a model's pick from it comes back here with `chosenPath`, which is no new drop).
    if (!chosenPath) {
      const model = dropped.find((item) => isModelFile(item.file.name));
      const zip = dropped.some((item) => fileExtension(item.file.name) === '.zip');
      this.productAnalytics.track('upload_selected', {
        params: {
          format: model ? fileExtension(model.file.name).slice(1) : zip ? 'zip' : 'none',
          files: dropped.length,
          size_mb: productMegabytes(dropped.reduce((sum, item) => sum + item.file.size, 0)),
          zip,
          adding_textures: !!gap,
        },
      });
    }
    // An archive is read in small chunks: outside Angular, so each one does not re-render the page.
    const preparation = await this.ngZone.runOutsideAngular(() => prepareFiles(dropped, {
      chosenPath,
      // Textures that arrive while a model waits for them join it; a new model starts over.
      texturesOnly: !!gap,
      limitBytes: renderArchiveLimitBytes(),
      shouldStop: stale,
      onProgress: (progress) => this.ngZone.run(() => {
        if (!stale()) {
          this.unpacking = progress;
        }
      }),
    }));
    if (stale()) {
      return;
    }
    this.unpacking = null;
    if (preparation.kind === 'problem') {
      this.textureGap = null;
      this.problem = preparation.problem;
      this.productAnalytics.track('upload_rejected', { params: { reason: preparation.problem.code, fix: preparation.problem.fix } });
      if (preparation.report) {
        this.rollbar.info('Render upload: files not opened', preparation.report);
      }
      return;
    }
    if (preparation.kind === 'choose') {
      this.textureGap = null;
      this.modelChoice = { files: dropped, models: preparation.models, selected: preparation.models[0].path, source: preparation.source };
      return;
    }
    const joins = !!gap && !preparation.files.some((item) => isModelFile(item.file.name));
    const files = gap && joins ? withAddedFiles(gap.model, preparation.files) : preparation.files;
    const archive = joins ? gap?.archive : preparation.archive;
    const inspection = inspectUpload(files);
    if (inspection.ok === false) {
      this.textureGap = null;
      this.problem = inspection.problem;
      this.productAnalytics.track('upload_rejected', { params: { reason: inspection.problem.code, fix: inspection.problem.fix } });
      return;
    }
    // "From big.zip: 1 model and 14 textures." before the pick's own line.
    const textures = inspection.model.textures.length;
    const fromArchive = archive ? `From ${archive}: 1 model${textures ? ` and ${textures} texture${textures === 1 ? '' : 's'}` : ''}.` : '';
    const note = joins ? gap?.note : [fromArchive, preparation.note].filter(Boolean).join(' ') || undefined;
    const check = await checkModelTextures(inspection.model);
    if (stale() || this.busy) {
      return;
    }
    if (check.missing.length > 0) {
      const wasMissing = joins && gap ? gap.missing.filter((name) => !check.missing.includes(name)) : [];
      this.textureGap = { model: check.model, missing: check.missing, found: check.found, added: [...(joins && gap ? gap.added : []), ...wasMissing], note, archive };
      this.productAnalytics.track('missing_textures_shown', {
        params: { count: check.missing.length, found: check.found.length, adding_textures: joins, exporter: check.model.fbx?.exporter?.app },
      });
      return;
    }
    this.beginUpload({ upload: check.model }, undefined, note);
  }

  // The zone's Cancel while a sample's thumbnail is still in the air.
  public cancelUpload(): void {
    this.run++;
    this.upload = null;
  }

  // The upload starts and the model's page opens: its stages card shows the upload from here on.
  // A sample's thumbnail first lands in the zone (the drop done for the user).
  private async beginUpload(input: RenderPreviewInput, flyFrom?: HTMLImageElement, note?: string): Promise<void> {
    this.problem = null;
    this.hint = null;
    this.textureGap = null;
    this.modelChoice = null;
    this.unpacking = null;
    this.upload = null;
    const run = ++this.run;

    if (flyFrom && 'sample' in input) {
      this.upload = {
        fileName: input.sample.fileName,
        poster: input.sample.stills.landscapeCard,
        format: fileExtension(input.sample.fileName).slice(1).toUpperCase(),
        sentBytes: 0,
        totalBytes: input.sample.fileBytes,
        done: false,
      };
      await this.zone?.receive(flyFrom);
      // Cancelled, or the page left, while the thumbnail was in the air.
      if (run !== this.run || this.destroyed) {
        return;
      }
    }

    const modelId = this.flow.beginUpload(input, note);
    if (modelId) {
      this.router.navigateByUrl(`/render/processing/${modelId}`);
      return;
    }
    this.upload = null;
    this.problem = { code: 'upload-failed', title: 'The upload stopped.', text: this.flow.takeUploadFailure() || 'Try again.' };
  }

  private syncInFlight(): void {
    const inFlight = this.uploadInFlight;
    if (inFlight !== this.inFlight.value) {
      this.inFlight.next(inFlight);
    }
    this.flow.setUploading(inFlight ? 'unpacking' : null);
  }
}
